import produce from 'immer';
import useObservable from 'packages/workspace/modules/helpers/hooks/useObservable';
import { createLogger } from 'packages/workspace/modules/prelude/logger/logger';
import type { Dispatch, SetStateAction } from 'react';
import { BehaviorSubject, Subscription } from 'rxjs';
import type { IOrganizationWorkspaceResource } from '../../modules/algebra/interfaces/IOrganizationWorkspaceResource';
import type { IamPrincipal } from '../models/session';
import { AuthUser } from '@aws-amplify/auth';

const fileLabel = 'session-store.ts';
const logger = createLogger({
  fileLabel,
});

export interface ISessionState {
  loggedIn?: boolean;
  iamPrincipal: IamPrincipal;
  cognitoUser: AuthUser;
  discoveryParams: IOrganizationWorkspaceResource;
}

export const initialState: ISessionState = {
  loggedIn: false,
  iamPrincipal: null as unknown as IamPrincipal,
  cognitoUser: null as unknown as AuthUser,
  discoveryParams: null as unknown as IOrganizationWorkspaceResource,
};

const session$ = new BehaviorSubject(initialState);

export const sessionStore = {
  /**
   * Subscribtion Function.
   * @param setState
   */
  subscribe: (setState: Dispatch<SetStateAction<ISessionState>>): Subscription => {
    return session$.subscribe({
      next: setState,
      error: (error) => logger.error(error),
    });
  },
  /**
   *
   * @param user
   */
  setAuthUser: (user: IamPrincipal): void => {
    const state = produce(session$.value, (draftState) => {
      draftState.iamPrincipal = user;
      draftState.loggedIn = true;
    });
    session$.next(state);
  },
  /**
   * Update Cognito User
   * @param cognitoUser
   */
  setCognitoUser: (cognitoUser: AuthUser): void => {
    const state = produce(session$.value, (draftState) => {
      draftState.cognitoUser = cognitoUser;
    });
    session$.next(state);
  },
  updateLogin: (iamPrincipal: IamPrincipal, cognitoUser: AuthUser): void => {
    const state = produce(session$.value, (draftState) => {
      draftState.cognitoUser = cognitoUser;
      draftState.iamPrincipal = iamPrincipal;
      draftState.loggedIn = true;
    });
    session$.next(state);
  },
  /**
   * Set discovery Params
   */
  updateDiscoveryParams: (discoveryParams: IOrganizationWorkspaceResource): void => {
    const state = produce(session$.value, (draftState) => {
      draftState.discoveryParams = discoveryParams;
    });
    session$.next(state);
  },
  /**
   * Set discovery Params
   */
  consentAccepted: (): void => {
    const state = produce(session$.value, (draftState) => {
      draftState.iamPrincipal.security.security_consents_approved = true;
    });
    session$.next(state);
  },
  /**
   * CLEAR Store on Logout .
   */
  clearStore: (): void => {
    const state = produce(session$.value, () => initialState);
    session$.next(state);
  },
  unsetDiscoveryParams: () => {
    const state = produce(session$.value, (draftState) => {
      draftState.discoveryParams = null as unknown as IOrganizationWorkspaceResource;
    });
    session$.next(state);
  },
};

/**
 * Hook Usage everywhere.
 */

export default function useSession(): ISessionState {
  return useObservable<ISessionState>(session$, initialState);
}
